import { memo, useCallback } from "react";
import { useLocation } from "react-router";

import { SkeletonAwait } from "~/components/suspense/skeleton";
import { HybridLink } from "~/hec/components/ui/hybrid-link";
import type {
  CioFacetState,
  SelectedCioFacets,
} from "~/hec/context/cio-facet-adapter";
import {
  SET_CIO_SELECTED_FACETS,
  useCioFacetSelector,
} from "~/hec/context/cio-facet-adapter";
import type { CategoryFiltersProps, NormalizedCategory } from "~/hec/types/plp";
import { BANNER_TYPES } from "~/hec/types/plp";
import { cn } from "~/lib/ui";
import { applyCategorySlugMap, serializeSearchParams } from "~/lib/utils/url";
import { useLayoutData } from "~/routes/layout";

import { applyFilter } from "./utils";

const PILL_CLASS =
  "flex flex-shrink-0 flex-grow items-center justify-center gap-2 px-5 h-[36px] rounded-full border-[0.5px] border-black bg-gray-01 font-grotesque text-sm font-light text-black whitespace-nowrap overflow-hidden cursor-pointer hover:bg-gray-02 active:bg-gray-03 active:text-white active:border-gray-03";

const PILL_SELECTED_CLASS =
  "flex flex-shrink-0 flex-grow items-center justify-center gap-2 px-5 h-[36px] rounded-full border-[0.5px] border-gray-03 bg-gray-03 font-grotesque text-sm font-light text-white whitespace-nowrap overflow-hidden cursor-pointer";

export function CategoryFilters({
  attributes,
  className,
  filterType,
  list,
  links,
  facets,
  layoutType,
  subcategories,
}: CategoryFiltersProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const slugMap = useLayoutData().categorySlugMap;

  const allFacets = useCioFacetSelector(
    ctx => ctx.state.cioFacets,
  ) as CioFacetState["cioFacets"];
  const selectedCioFacets = useCioFacetSelector(
    ctx => ctx.state.selectedCioFacets,
  ) as SelectedCioFacets;
  const dispatch = useCioFacetSelector(ctx => ctx.dispatch);

  const toggleFacetOption = useCallback(
    (facetName: string, option: { value: string; display_name: string }) => {
      const current = selectedCioFacets[facetName] || [];
      const isSelected = current.some(v => v.value === option.value);
      dispatch({
        type: SET_CIO_SELECTED_FACETS,
        payload: {
          ...selectedCioFacets,
          [facetName]: isSelected
            ? current.filter(v => v.value !== option.value)
            : [...current, option],
        },
      });
    },
    [selectedCioFacets, dispatch],
  );

  return (
    <div
      {...attributes}
      className={cn(
        "w-full py-3",
        filterType === BANNER_TYPES.FILTERS
          ? "lg:scrollbar-none grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:flex lg:flex-nowrap lg:items-center lg:gap-2 lg:overflow-x-auto"
          : layoutType === "scroll"
            ? "scrollbar-none flex flex-nowrap gap-2 overflow-x-auto"
            : "flex flex-wrap gap-2",
        className,
      )}
      data-testid="category-filters"
    >
      {filterType === BANNER_TYPES.LINK_AND_IMAGE &&
        links?.map((item, index) => (
          <a
            key={`category-filter-${index}`}
            href={item["link"]}
            aria-label={`Filter by "${item["label"]}"`}
            data-testid="category-filters-link"
            className={PILL_CLASS}
          >
            {item["image"] && (
              <img
                src={item["image"]}
                alt=""
                aria-hidden="true"
                className="h-6 w-6 rounded-full object-cover"
              />
            )}
            {item["label"]}
          </a>
        ))}

      {filterType === BANNER_TYPES.FILTERS &&
        list?.map((item, index) => {
          const pillPmin = item["minimumPrice"] ?? "";
          const pillPmax = item["maximumPrice"] ?? "";
          const isSelected =
            (searchParams.get("pmin") ?? "") === pillPmin &&
            (searchParams.get("pmax") ?? "") === pillPmax &&
            (pillPmin !== "" || pillPmax !== "");

          // Link, not button + navigate(): a pre-hydration click on a button
          // has no href to follow and no handler yet, so it dies silently.
          return (
            <HybridLink
              key={`category-filter-${index}`}
              to={{
                pathname: location.pathname,
                search: `?${serializeSearchParams(applyFilter(searchParams, item))}`,
              }}
              aria-label={`Filter by "${item["filterLabel"]}"`}
              // aria-pressed is for toggle buttons; a link's selected state is aria-current.
              aria-current={isSelected ? "true" : undefined}
              data-testid="category-filters-link"
              className={isSelected ? PILL_SELECTED_CLASS : PILL_CLASS}
            >
              {item["filterLabel"]}
            </HybridLink>
          );
        })}

      {filterType === BANNER_TYPES.FACETS &&
        facets?.map(({ name, value, displayName }) => {
          const facetData = allFacets.find(f => f.name === name);
          const validOption = facetData?.options.find(o => o.value === value);
          if (!facetData || !validOption) return null;
          // SFCC-11411: the Builder "PLP Filter Pills" data model stores the
          // per-pill label override under `displayName` (camelCase). Honor it
          // first so a merchandiser-entered name takes priority over the CIO
          // facet display name; fall back to CIO only when it's blank.
          const label = displayName || validOption.display_name;
          const isSelected = (selectedCioFacets[name] || []).some(
            v => v.value === value,
          );
          return (
            <button
              key={`facet-${name}-${value}`}
              onClick={() =>
                toggleFacetOption(name, { value, display_name: label })
              }
              aria-label={`Filter by "${label}"`}
              aria-pressed={isSelected}
              data-testid="category-filters-facet"
              className={isSelected ? PILL_SELECTED_CLASS : PILL_CLASS}
            >
              {label}
            </button>
          );
        })}

      {filterType === BANNER_TYPES.CATEGORIES && (
        <SkeletonAwait resolve={slugMap} skeleton={null}>
          {resolvedSlugMap =>
            subcategories?.map((item: NormalizedCategory, index: number) => {
              // Prefer the loader-computed full path so siblings of an
              // `all-X` view-all category land at `/X/sibling`, not at
              // `/X/all-X/sibling`. Fall back to the historical
              // pathname-concat heuristic only when path is absent.
              //
              // SFCC-10716: rewrite each segment id through the layout's
              // slug map so categories with `c_alternativeUrl` use their
              // production URL slug instead of the catalog id. The slug-mapped
              // href is also what `isActive` compares against — comparing the
              // raw `item.path` would fail on categories with alt URLs.
              const href = item.path
                ? `/${applyCategorySlugMap(item.path, resolvedSlugMap)}`
                : `${location.pathname}/${item.categoryShortName}`;
              const isActive = href === location.pathname;

              return (
                <HybridLink
                  key={`category-filter-${index}-${item.id}`}
                  to={href}
                  prefetch="intent"
                  aria-label={`Filter by "${item.name}"`}
                  data-testid="category-filters-link"
                  className={isActive ? PILL_SELECTED_CLASS : PILL_CLASS}
                >
                  {item.categoryIcon && (
                    <img
                      src={item.categoryIcon}
                      alt=""
                      aria-hidden="true"
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  )}
                  {item.name}
                </HybridLink>
              );
            })
          }
        </SkeletonAwait>
      )}
    </div>
  );
}

CategoryFilters.displayName = "CategoryFilters";

export default memo(CategoryFilters);
